import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import {
  useBookingIntentStatus,
  localizeBookingError,
  parseBookingError,
} from '@cleverbooking-fe-monorepo/booking-payment';
import { SiteShell } from '../components/site-shell';

/**
 * Return URL for async Stripe payment methods (Pix/Bancontact). Stripe
 * appends `?payment_intent=pi_...&payment_intent_client_secret=...&redirect_status=...`
 * when it redirects back. We read `payment_intent` and poll the booking-intent
 * status until it reaches a terminal state, then show the outcome.
 *
 * (Inline card payments resolve on the salon dialog itself and never reach
 * this page — but landing here directly is handled gracefully too.)
 */
export function BookingCompletePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const paymentIntentId = params.get('payment_intent');
  // Stripe appends redirect_status on async-method returns. Anything other than
  // "succeeded" (e.g. "failed") means the payment never went through — there is
  // no booking to poll for, so we short-circuit to the failure state instead of
  // spinning forever waiting for a status that will never flip.
  const redirectStatus = params.get('redirect_status');
  const redirectFailed =
    redirectStatus != null && redirectStatus !== 'succeeded';

  const statusQuery = useBookingIntentStatus(paymentIntentId);
  const state = statusQuery.data?.state;
  const bookingReference = statusQuery.data?.bookingReference;
  // A status-query error means the PaymentIntent is unknown/expired (the
  // /status endpoint 404s) — treat it as terminal so the page stops spinning.
  // Prefer the backend's coded/localized message when the body carries one.
  const statusErrored = statusQuery.isError;
  const erroredMessage = statusQuery.isError
    ? localizeBookingError(parseBookingError(statusQuery.error), t)
    : null;
  const isFailureState =
    state === 'failed' || redirectFailed || statusErrored;

  return (
    <SiteShell>
      <section className="max-w-md mx-auto px-6 py-20 md:py-28 text-center">
        {!paymentIntentId ? (
          <Panel
            tone="error"
            title={t('bookingComplete.missingTitle', 'Nothing to confirm')}
            body={t(
              'bookingComplete.missingBody',
              "We couldn't find a payment to confirm. If you just booked, check your email for the confirmation.",
            )}
          >
            <HomeLink label={t('bookingComplete.browse', 'Browse salons')} />
          </Panel>
        ) : state === 'booked' && !redirectFailed ? (
          <Panel
            tone="success"
            title={t('bookingComplete.bookedTitle', 'Booking confirmed')}
            body={t(
              'bookingComplete.bookedBody',
              'Your payment went through and your booking is confirmed. A confirmation email is on its way.',
            )}
          >
            {bookingReference ? (
              <p className="text-sm text-muted-foreground">
                {t('bookingComplete.reference', 'Reference')}:{' '}
                <span className="font-mono text-foreground">{bookingReference}</span>
              </p>
            ) : null}
            <div className="flex flex-col gap-2 mt-4">
              <Link
                to="/me/bookings"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
              >
                {t('bookingComplete.myBookings', 'See my bookings')}
              </Link>
              <HomeLink label={t('bookingComplete.browse', 'Browse salons')} muted />
            </div>
          </Panel>
        ) : state === 'refunded' ? (
          <Panel
            tone="error"
            title={t('bookingComplete.refundedTitle', 'That slot was just taken')}
            body={t(
              'bookingComplete.refundedBody',
              "Someone grabbed that time before your payment finished, so you've been fully refunded. Please pick another slot.",
            )}
          >
            <HomeLink label={t('bookingComplete.browse', 'Browse salons')} />
          </Panel>
        ) : isFailureState ? (
          <Panel
            tone="error"
            title={t('bookingComplete.failedTitle', 'Payment failed')}
            body={
              erroredMessage ??
              t(
                'bookingComplete.failedBody',
                "Your payment didn't go through, so no booking was made. Please try again.",
              )
            }
          >
            <HomeLink label={t('bookingComplete.browse', 'Browse salons')} />
          </Panel>
        ) : (
          // pending (or first load before the first poll resolves)
          <div className="flex flex-col items-center gap-4 py-8">
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-foreground" />
            <p className="text-sm text-muted-foreground">
              {t('bookingComplete.confirming', 'Confirming your booking…')}
            </p>
          </div>
        )}
      </section>
    </SiteShell>
  );
}

function Panel({
  tone,
  title,
  body,
  children,
}: {
  tone: 'success' | 'error';
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div
        className={[
          'w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5',
          tone === 'success' ? 'bg-primary/10' : 'bg-secondary',
        ].join(' ')}
      >
        {tone === 'success' ? (
          <CheckCircle2 className="w-7 h-7 text-primary" />
        ) : (
          <AlertCircle className="w-7 h-7 text-muted-foreground" />
        )}
      </div>
      <h1 className="text-2xl font-bold text-foreground mb-2">{title}</h1>
      <p className="text-sm text-muted-foreground leading-relaxed mb-6">{body}</p>
      {children}
    </div>
  );
}

function HomeLink({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <Link
      to="/search"
      className={
        muted
          ? 'inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground transition-colors'
          : 'inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity'
      }
    >
      <RotateCcw className="w-4 h-4" />
      {label}
    </Link>
  );
}

export default BookingCompletePage;
